import React, { useCallback, useEffect, useMemo } from 'react'
import {
  FocusContext,
  useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import { useNavigate } from 'react-router-dom'

import FocusableButtonTS from '../FocusableButton/FocusableButtonTS'
import {
  getFeatures,
  getTTSText,
  getWithColumnHeader,
  getWithoutColumnHeader,
  getMoreInfoText,
  TITLE,
} from './utils'
import type { WhatIsPassportProps } from './types'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../shared/providers/KeyDownProvider'
import tts from '../../lib/TTS.js'
import googleAnalytics4 from '../../lib/googleAnalytics.js'

const GO_BACK_FOCUS_KEY = 'GO_BACK_BUTTON'

function WhatIsPassport({ displayUrl, stationDisplayName }: WhatIsPassportProps) {
  const { ref, focusKey } = useFocusable({
    focusKey: 'WHAT_IS_PASSPORT',
  })
  const navigate = useNavigate()

  const features = useMemo(() => getFeatures(stationDisplayName), [stationDisplayName])
  const withoutColumnHeader = useMemo(
    () => getWithoutColumnHeader(stationDisplayName),
    [stationDisplayName]
  )
  const withColumnHeader = useMemo(
    () => getWithColumnHeader(stationDisplayName),
    [stationDisplayName]
  )
  const moreInfoText = useMemo(
    () => getMoreInfoText(displayUrl),
    [displayUrl]
  )

  // Workaround to allow new focus components to work with spatial navigation
  // and old components work with focusable HoC
  // TODO: remove this when whole app is migrated to react spatial navigation
  useEffect(() => {
    window.focusedComponent = {}
    window.focusedComponent.handleBySpatialNavigation = true
    return () => {
      window.focusedComponent.handleBySpatialNavigation = false
    }
  }, [stationDisplayName, moreInfoText])

  useEffect(() => {
    tts.pushTextToRead(getTTSText(stationDisplayName))
    tts.enqueueTextToRead(moreInfoText)
    googleAnalytics4.sendPageViewEvent('Passport Details')
    return () => {
      tts.stop()
    }
  }, [stationDisplayName, moreInfoText])

  const goBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const onGoBackFocus = useCallback(() => {
    tts.stop()
    tts.addTextToRead('Focused on Go Back button')
  }, [])

  useKeyDown(KeyTypeEnum.Back, goBack, { stopPropagation: true })

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className="what-is-passport">
        <h1 className="what-is-passport__title">{TITLE}</h1>

        <div className="what-is-passport__table">
          <div className="what-is-passport__row what-is-passport__row--header">
            <div className="what-is-passport__cell what-is-passport__cell--label" />
            <div className="what-is-passport__cell what-is-passport__cell--center">
              {withoutColumnHeader}
            </div>
            <div className="what-is-passport__cell what-is-passport__cell--center what-is-passport__cell--bold">
              {withColumnHeader}
            </div>
          </div>

          {features.map((feature) => (
            <div key={feature.label} className="what-is-passport__row">
              <div className="what-is-passport__cell what-is-passport__cell--label">
                {feature.label}
              </div>
              <div className="what-is-passport__cell what-is-passport__cell--center">
                {feature.withoutPassport && (
                  <span className="what-is-passport__check" />
                )}
              </div>
              <div className="what-is-passport__cell what-is-passport__cell--center">
                {feature.withPassport && (
                  <span className="what-is-passport__check" />
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="what-is-passport__footer">
          <FocusableButtonTS
            focusKey={GO_BACK_FOCUS_KEY}
            onEnterPress={goBack}
            onFocus={onGoBackFocus}
            stealFocus
            className="what-is-passport__button"
          >
            Go Back
          </FocusableButtonTS>
          <span className="what-is-passport__info-text">{moreInfoText}</span>
        </div>
      </div>
    </FocusContext.Provider>
  )
}

export default WhatIsPassport
